/**
 * Home-local line icons, ported from
 * claude-design/orborix-speak/project/home-screen.jsx.
 *
 * Only the icons the Home actually renders live here. The bottom-bar icons
 * (chat/book/stats/user) live in the shared TabBar (components/nav/TabBar).
 */

interface IconProps {
  size?: number;
}

export function IconFire({ size = 16, color = '#ffb863' }: IconProps & { color?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
      <path d="M12 2 C 13 6, 16 7, 17 11 C 18 14, 17 18, 14 20 C 16 18, 15 14, 13 13 C 14 16, 11 18, 10 16 C 9 18, 7 17, 7 14 C 7 11, 10 9, 12 2 Z" />
    </svg>
  );
}

export function IconArrowRight({ size = 18 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12 H 19" />
      <path d="M13 6 L 19 12 L 13 18" />
    </svg>
  );
}

export function IconLock({ size = 14 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11 V 8 a 4 4 0 0 1 8 0 V 11" />
    </svg>
  );
}

// ─── Iconos de los 3 ejes de aprendizaje ─────────────────────────────────────

/** Conversación libre → burbuja de chat. */
export function IconChat({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <path d="M21 12 a 9 9 0 0 1 -13 8 L 3 21 L 4 17 A 9 9 0 1 1 21 12 Z" />
    </svg>
  );
}

/** Práctica de escenarios → diana (misión / objetivo). */
export function IconTarget({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Escuchar → auriculares (resúmenes de libros en audio). */
export function IconHeadphones({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round">
      <path d="M4 14 v -2 a 8 8 0 0 1 16 0 v 2" />
      <rect x="3" y="13" width="4.5" height="7.5" rx="1.6" />
      <rect x="16.5" y="13" width="4.5" height="7.5" rx="1.6" />
    </svg>
  );
}

/** Programa guiado → birrete (curso por niveles). */
export function IconCap({ size = 22 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round">
      <path d="M2.5 9 L 12 4.5 L 21.5 9 L 12 13.5 Z" />
      <path d="M6.5 11 V 16 c 0 1.3 2.8 2.8 5.5 2.8 s 5.5 -1.5 5.5 -2.8 V 11" />
      <path d="M21.5 9 V 14" />
    </svg>
  );
}

// ─── Iconos del rediseño (hero "Continuá" + stats + hoja de racha) ───────────

/** Play (triángulo lleno) — botón del hero. */
export function IconPlay({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none">
      <path d="M7 4 L 20 12 L 7 20 Z" />
    </svg>
  );
}

/** Pause (dos barras) — botón del hero cuando ya está reproduciendo. */
export function IconPause({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none">
      <rect x="6" y="4" width="4.2" height="16" rx="1.2" />
      <rect x="13.8" y="4" width="4.2" height="16" rx="1.2" />
    </svg>
  );
}

/** Chispa (estrella de 4 puntas) — eyebrow del hero. */
export function IconSpark({ size = 13 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" stroke="none">
      <path d="M12 2 L 13.6 9 L 21 11 L 13.6 13 L 12 20 L 10.4 13 L 3 11 L 10.4 9 Z" />
    </svg>
  );
}

/** Capas — tile de "Frontera" (bandas de vocabulario). */
export function IconLayers({ size = 13 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 L 21 8 L 12 13 L 3 8 Z" />
      <path d="M3 12 L 12 17 L 21 12" />
    </svg>
  );
}

/** Reloj — tile de "Hoy" (minutos). */
export function IconClock({ size = 13 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7 V 12 L 15 14" />
    </svg>
  );
}

/** Chevron izquierda — navegación de mes + volver del detalle. */
export function IconChevronLeft({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6 L 9 12 L 15 18" />
    </svg>
  );
}

/** Chevron derecha — navegación de mes. */
export function IconChevronRight({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6 L 15 12 L 9 18" />
    </svg>
  );
}

/** Check — estado "hoy ya cuenta" en la hoja de racha. */
export function IconCheck({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5 L 10 17 L 19 7" />
    </svg>
  );
}

/** Más — stat "palabras usadas" en el detalle del día. */
export function IconPlus({ size = 15 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5 V 19 M5 12 H 19" />
    </svg>
  );
}

/** Cerrar (X) — botón de cierre de la hoja de racha. */
export function IconClose({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6 L 18 18 M18 6 L 6 18" />
    </svg>
  );
}

// ─── Iconos del Home v2 (cards hero, handoff design_handoff_home_v2) ─────────

/** Micrófono — CTA "Empezar conversación". */
export function IconMic({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M6 11 a 6 6 0 0 0 12 0" />
      <path d="M12 17 V 21" />
    </svg>
  );
}

/** Casa — tile de escenario "Día a día". */
export function IconHome({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11 L 12 4 L 20 11" />
      <path d="M6 9.5 V 19 a 1 1 0 0 0 1 1 H 17 a 1 1 0 0 0 1 -1 V 9.5" />
      <path d="M10 20 V 14.5 h 4 V 20" />
    </svg>
  );
}

/** Libro abierto — cover de la fila de unidad del Programa. */
export function IconBook({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <path d="M4 4 V 20 A 1 1 0 0 0 5 21 H 12 V 5 A 2 2 0 0 0 10 3 H 5 A 1 1 0 0 0 4 4 Z" />
      <path d="M20 4 V 20 A 1 1 0 0 1 19 21 H 12 V 5 A 2 2 0 0 1 14 3 H 19 A 1 1 0 0 1 20 4 Z" />
    </svg>
  );
}
